import { createFileRoute, redirect, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { AppShell } from '@tupai/shell';
import { useAuthStore } from '@/store/auth-store';
import PhoneAuthForm from '@/components/phone-auth-form';
import { EmailAuthForm } from '@/components/email-auth-form';
import BrandHeader from '@/components/brand-header';
import { useTranslation } from '@tupai/i18n/react';
import { signedInPath } from '@/lib/invite-flow';
import { IconMessageCircle, IconMail } from '@tabler/icons-react';

export const Route = createFileRoute('/signup')({
  beforeLoad: () => {
    if (useAuthStore.getState().token) throw redirect({ to: signedInPath(), replace: true });
  },
  component: SignupPage,
});

function SignupPage() {
  const [step, setStep] = useState<'picker' | 'phone' | 'otp' | 'email'>('picker');
  const { t } = useTranslation('auth');

  return (
    <AppShell
      variant="fullscreen"
      tone="surface"
      className="relative items-center justify-center overflow-y-auto px-8 py-8"
    >
      {step === 'picker' && (
        <div className="flex w-full max-w-sm flex-col items-center">
          <div className="mb-10 flex flex-col items-center gap-4">
            <BrandHeader />
            <p className="font-display text-xl text-content text-center">
              {t('signupTagline1')}
              <br />
              {t('signupTagline2')}
            </p>
          </div>

          <div className="flex w-full flex-col gap-3">
            <button
              onClick={() => setStep('phone')}
              className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-md border-[1.5px] border-border bg-surface font-bold text-content hover:bg-bg transition-colors"
            >
              <IconMessageCircle size={20} /> {t('signUpWithPhone')}
            </button>

            <button
              onClick={() => setStep('email')}
              className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-md bg-primary font-bold text-surface hover:brightness-95 transition-colors"
            >
              <IconMail size={20} /> {t('signUpWithEmail')}
            </button>
          </div>

          <p className="text-sm text-muted text-center mt-6">
            {t('haveAccount')}{' '}
            <Link
              to="/login"
              className="inline-flex min-h-11 items-center px-1 font-bold text-primary"
            >
              {t('logIn')}
            </Link>
          </p>

          <p className="text-sm text-hint mt-4 text-center max-w-xs">
            {t('termsPrefix')}{' '}
            <a
              href="https://tupai.ai/terms-of-service/"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              {t('termsOfService')}
            </a>{' '}
            {t('termsAnd')}{' '}
            <a
              href="https://tupai.ai/privacy-policy/"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              {t('privacyPolicy')}
            </a>
            .
          </p>
        </div>
      )}

      {(step === 'phone' || step === 'otp') && (
        <>
          {step === 'phone' && (
            <div className="mb-10 flex flex-col items-center gap-4">
              <BrandHeader />
              <p className="font-display text-xl text-content text-center">
                {t('signupTagline1')}
                <br />
                {t('signupTagline2')}
              </p>
            </div>
          )}

          <PhoneAuthForm mode="signup" onStepChange={setStep} />

          {step === 'phone' && (
            <button
              onClick={() => setStep('picker')}
              className="text-sm font-bold text-muted hover:text-primary transition-colors mt-6"
            >
              {t('backToOptions')}
            </button>
          )}
        </>
      )}

      {step === 'email' && <EmailAuthForm mode="signup" onBack={() => setStep('picker')} />}
    </AppShell>
  );
}
